/**
 * InlineOpenQuestionsPanel
 *
 * A compact panel for surfacing Open Questions anchored to a specific
 * clarity artifact (desired_outcome, outcome_driver, backplan_node, ost_node)
 * directly within that artifact's editor.
 *
 * Implements the "Open Questions — ambient across everything" relationship
 * from the Clarity Tools Relatedness plan.
 */

import { useEffect, useState } from 'react';
import { HelpCircle, Plus, Check, XCircle, RotateCcw, Loader2, X } from 'lucide-react';
import {
  useOpenQuestionsStore,
  STATUS_TRANSITIONS,
  type OpenQuestion,
  type OpenQuestionStatus,
  type OpenQuestionContextType,
} from '../../stores/prioritization/openQuestionsStore';

// ── Status badge ───────────────────────────────────────────────

const STATUS_COLORS: Record<OpenQuestionStatus, string> = {
  open: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',
  in_progress: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]',
  resolved: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]',
  deferred: 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)]/40',
};

const STATUS_LABELS: Record<OpenQuestionStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  deferred: 'Deferred',
};

// ── Inline question row ────────────────────────────────────────

function QuestionRow({ question }: { question: OpenQuestion }) {
  const { transitionStatus, deleteQuestion } = useOpenQuestionsStore();
  const [showResolveInput, setShowResolveInput] = useState(false);
  const [resolutionText, setResolutionText] = useState('');
  const [isActing, setIsActing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const allowed = STATUS_TRANSITIONS[question.status];

  async function handleResolve() {
    if (!resolutionText.trim()) return;
    setIsActing(true);
    await transitionStatus(question.id, 'resolved', resolutionText.trim());
    setIsActing(false);
    setShowResolveInput(false);
    setResolutionText('');
  }

  async function handleTransition(newStatus: OpenQuestionStatus) {
    if (newStatus === 'resolved') { setShowResolveInput(true); return; }
    setIsActing(true);
    await transitionStatus(question.id, newStatus);
    setIsActing(false);
  }

  return (
    <div className={` px-3 py-2.5 border text-sm transition-all
      ${question.status === 'resolved'
        ? 'border-[var(--green)] dark:border-[var(--green)]/50 bg-[var(--green-dim)]/50 dark:bg-[var(--green)]/10 opacity-70'
        : 'border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)]'}`}
    >
      <div className="flex items-start gap-2">
        <HelpCircle className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0
          ${question.status === 'resolved' ? 'text-[var(--green)]' : 'text-[var(--text-ter)]'}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm leading-snug text-[var(--text-pri)] dark:text-[var(--text-pri)]
              ${question.status === 'resolved' ? 'line-through text-[var(--text-ter)]' : ''}`}
            >
              {question.text}
            </p>
            <span className={`flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[question.status]}`}>
              {STATUS_LABELS[question.status]}
            </span>
          </div>

          {question.status === 'resolved' && question.resolutionText && (
            <p className="mt-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
              ✓ {question.resolutionText}
            </p>
          )}

          {showResolveInput && (
            <div className="mt-2">
              <input
                autoFocus
                type="text"
                value={resolutionText}
                onChange={(e) => setResolutionText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleResolve(); if (e.key === 'Escape') setShowResolveInput(false); }}
                placeholder="How was this resolved?"
                className="w-full px-2.5 py-1.5 text-xs border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <div className="flex gap-2 mt-1.5">
                <button
                  onClick={handleResolve}
                  disabled={!resolutionText.trim() || isActing}
                  className="flex items-center gap-1 px-2 py-1 bg-[var(--green)] hover:bg-[var(--green)] disabled:opacity-60 text-white text-xs font-medium rounded transition-colors"
                >
                  {isActing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  Resolve
                </button>
                <button onClick={() => setShowResolveInput(false)} className="px-2 py-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">Cancel</button>
              </div>
            </div>
          )}

          {!showResolveInput && (
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              {allowed.includes('in_progress') && question.status === 'open' && (
                <button onClick={() => handleTransition('in_progress')} disabled={isActing}
                  className="text-[11px] text-[var(--mustard)] hover:text-[var(--mustard)] disabled:opacity-60">
                  Start
                </button>
              )}
              {allowed.includes('resolved') && (
                <button onClick={() => handleTransition('resolved')} disabled={isActing}
                  className="text-[11px] text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] disabled:opacity-60 flex items-center gap-0.5">
                  <Check className="h-3 w-3" /> Resolve
                </button>
              )}
              {allowed.includes('deferred') && (
                <button onClick={() => handleTransition('deferred')} disabled={isActing}
                  className="text-[11px] text-[var(--text-ter)] hover:text-[var(--text-sec)] disabled:opacity-60 flex items-center gap-0.5">
                  <XCircle className="h-3 w-3" /> Defer
                </button>
              )}
              {allowed.includes('open') && question.status !== 'open' && (
                <button onClick={() => handleTransition('open')} disabled={isActing}
                  className="text-[11px] text-[var(--text-ter)] hover:text-[var(--text-ter)] disabled:opacity-60 flex items-center gap-0.5">
                  <RotateCcw className="h-3 w-3" /> Reopen
                </button>
              )}
              <span className="flex-1" />
              {confirmDel ? (
                <span className="flex items-center gap-1.5 text-[11px]">
                  <span className="text-[var(--coral)]">Delete?</span>
                  <button onClick={() => deleteQuestion(question.id)} className="text-[var(--coral)] underline">Yes</button>
                  <button onClick={() => setConfirmDel(false)} className="text-[var(--text-ter)]">No</button>
                </span>
              ) : (
                <button onClick={() => setConfirmDel(true)}
                  className="text-[11px] text-[var(--text-ter)] hover:text-[var(--coral)] dark:hover:text-[var(--coral)] transition-colors">
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Add question form ─────────────────────────────────────────

function AddQuestionInlineForm({
  entityType,
  entityId,
  onClose,
}: {
  entityType: OpenQuestionContextType;
  entityId: string;
  onClose: () => void;
}) {
  const { createQuestion } = useOpenQuestionsStore();
  const [text, setText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  async function handleAdd() {
    if (!text.trim()) return;
    setIsSaving(true);
    await createQuestion({
      text: text.trim(),
      contextEntityType: entityType,
      contextEntityId: entityId,
    });
    setIsSaving(false);
    onClose();
  }

  return (
    <div className="mt-2 flex items-start gap-2">
      <HelpCircle className="h-3.5 w-3.5 text-[var(--text-ter)] mt-2 flex-shrink-0" />
      <div className="flex-1">
        <input
          autoFocus
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAdd();
            if (e.key === 'Escape') onClose();
          }}
          placeholder="What needs to be answered or clarified?"
          className="w-full px-3 py-2 text-sm border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <div className="flex gap-2 mt-1.5">
          <button
            onClick={handleAdd}
            disabled={!text.trim() || isSaving}
            className="flex items-center gap-1 px-2.5 py-1 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-60 text-white text-xs font-medium rounded transition-colors"
          >
            {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            Add
          </button>
          <button onClick={onClose} className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">Cancel</button>
          <span className="text-[10px] text-[var(--text-ter)] self-center ml-auto">↵ to add · Esc to cancel</span>
        </div>
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────

interface InlineOpenQuestionsPanelProps {
  entityType: OpenQuestionContextType;
  entityId: string;
  accentColor?: 'green' | 'orange' | 'blue' | 'purple';
}

const ACCENT: Record<string, { header: string; button: string; border: string }> = {
  green: {
    header: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border-[var(--green)] dark:border-[var(--green)]',
    button: 'text-[var(--green)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] dark:text-[var(--text-ter)]',
    border: 'border-[var(--green)] dark:border-[var(--green)]',
  },
  orange: {
    header: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border-[var(--mustard)] dark:border-[var(--mustard)]',
    button: 'text-[var(--mustard)] hover:text-[var(--mustard)] dark:text-[var(--mustard)]',
    border: 'border-[var(--mustard)] dark:border-[var(--mustard)]',
  },
  blue: {
    header: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border-[var(--slate)] dark:border-[var(--slate)]',
    button: 'text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:text-[var(--text-ter)]',
    border: 'border-[var(--slate)] dark:border-[var(--slate)]',
  },
  purple: {
    header: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border-[var(--slate)] dark:border-[var(--slate)]',
    button: 'text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:text-[var(--text-ter)]',
    border: 'border-[var(--slate)] dark:border-[var(--slate)]',
  },
};

export function InlineOpenQuestionsPanel({
  entityType,
  entityId,
  accentColor = 'blue',
}: InlineOpenQuestionsPanelProps) {
  const { questions, syncState, fetchFromSupabase, getQuestionsForEntity } = useOpenQuestionsStore();
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (questions.length === 0 && !syncState.isLoading && !syncState.lastSyncAt) {
      fetchFromSupabase();
    }
  }, [questions.length, syncState.isLoading, syncState.lastSyncAt, fetchFromSupabase]);

  const anchored = getQuestionsForEntity(entityType, entityId);
  const open = anchored.filter((q) => q.status === 'open' || q.status === 'in_progress');
  const closed = anchored.filter((q) => q.status === 'resolved' || q.status === 'deferred');
  const colors = ACCENT[accentColor];

  return (
    <div className={`border  overflow-hidden ${colors.border}`}>
      <div className={`flex items-center justify-between px-4 py-3 border-b ${colors.header} ${colors.border}`}>
        <div className="flex items-center gap-2">
          <HelpCircle className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            Open Questions
            {anchored.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] text-[10px] font-bold">
                {anchored.length}
              </span>
            )}
          </span>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className={`flex items-center gap-1 text-xs font-medium transition-colors ${colors.button}`}
        >
          {showForm ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
          {showForm ? 'Cancel' : 'Add question'}
        </button>
      </div>

      <div className="px-4 py-3 bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
        {showForm && (
          <AddQuestionInlineForm
            entityType={entityType}
            entityId={entityId}
            onClose={() => setShowForm(false)}
          />
        )}

        {anchored.length === 0 && !showForm && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] py-1">
            No open questions yet. Add one to capture what needs to be answered.
          </p>
        )}

        {open.length > 0 && (
          <div className="space-y-2 mt-2">
            {open.map((q) => <QuestionRow key={q.id} question={q} />)}
          </div>
        )}

        {closed.length > 0 && (
          <details className="mt-3">
            <summary className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] cursor-pointer select-none hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]">
              {closed.length} resolved / deferred
            </summary>
            <div className="space-y-2 mt-2">
              {closed.map((q) => <QuestionRow key={q.id} question={q} />)}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
